import { useEffect, useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ApiReferenceReact } from '@scalar/api-reference-react'
import scalarStyleHref from '@scalar/api-reference-react/style.css?url'
import { ChevronLeft, Download } from 'lucide-react'
import { PageHeader } from '~/components/PageHeader'
import { Button } from '~/components/ui/button'
import { usePermissionGuard } from '~/lib/current-user'
import { seo } from '~/utils/seo'

const SPEC_URL = '/api/v1/openapi'

/**
 * Scalar's bundled stylesheet contains unscoped rules (body, html, :root,
 * scrollbars, fonts) that leak into every route once loaded. We attach it as
 * a route-scoped `<link>` via TanStack Router's head manager so the browser
 * drops the sheet when the user navigates away from /openapi.
 */
export const Route = createFileRoute('/openapi')({
  head: () => ({
    meta: seo({
      title: 'API Reference',
      description: 'Interactive reference for the First Response REST API.',
    }),
    links: [{ rel: 'stylesheet', href: scalarStyleHref }],
  }),
  component: OpenApiPage,
})

function OpenApiPage() {
  const allowed = usePermissionGuard('api:read')
  const darkMode = useDarkMode()
  useSuppressSameRouteReplaceState()

  if (!allowed) {
    return <div className="text-muted-foreground text-sm">Checking permissions...</div>
  }

  return (
    <div className="space-y-6">
      <Link
        to="/settings"
        search={{ tab: 'developer' }}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronLeft className="size-3.5" />
        Developer Options
      </Link>
      <PageHeader
        title="API Reference"
        description="Browse and try every endpoint under /api/v1. Authenticate with a Personal Access Token from Developer Options."
        actions={
          <Button asChild variant="outline" size="sm">
            <a href={SPEC_URL} download="rapid-response-openapi.json">
              <Download className="mr-2 h-4 w-4" />
              Download OpenAPI JSON
            </a>
          </Button>
        }
      />
      <div className="openapi-viewer rounded-md border bg-background">
        <ApiReferenceReact
          configuration={{
            url: SPEC_URL,
            darkMode,
            theme: 'none',
            hideDownloadButton: true,
            showSidebar: true,
            layout: 'modern',
            mcp: { disabled: true },
            authentication: { preferredSecurityScheme: 'apiToken' },
          }}
        />
      </div>
    </div>
  )
}

/**
 * Scalar fires a scroll-triggered `history.replaceState(..., '/openapi#tag/...')`
 * on every IntersectionObserver crossing so the URL reflects the visible
 * section. Inside TanStack Router's history stack that feedback causes two
 * adjacent sections to oscillate forever. We suppress only same-pathname
 * replaceState calls while this route is mounted; click-based navigation
 * uses `pushState` and is not affected, so deep links still work.
 */
function useSuppressSameRouteReplaceState(): void {
  useEffect(() => {
    const original = globalThis.history.replaceState.bind(globalThis.history)
    globalThis.history.replaceState = function patched(
      data: unknown,
      unused: string,
      url?: string | URL | null,
    ) {
      if (url != null) {
        try {
          const target = new URL(url.toString(), globalThis.location.href)
          if (
            target.origin === globalThis.location.origin &&
            target.pathname === globalThis.location.pathname
          ) {
            return
          }
        } catch {
          // fall through to native behavior
        }
      }
      return original(data, unused, url)
    }
    return () => {
      globalThis.history.replaceState = original
    }
  }, [])
}

function useDarkMode(): boolean {
  const [dark, setDark] = useState(false)

  useEffect(() => {
    const root = document.documentElement
    const sync = () => setDark(root.classList.contains('dark'))
    sync()
    const observer = new MutationObserver(sync)
    observer.observe(root, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])

  return dark
}
